import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Wifi, Home, Smartphone, Laptop, Tv, Router } from 'lucide-react';

/**
 * MeshAnimation component
 * Interactive animation that explains how mesh technology works
 */
const MeshAnimation = () => {
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Start the animation sequence
  const startAnimation = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      // Reset animation after completion
      setTimeout(() => setIsAnimating(false), 5000);
    }
  };

  // Device positions
  const devices = [
    { id: 'tv', icon: Tv, x: '20%', y: '30%', label: 'TV' },
    { id: 'laptop', icon: Laptop, x: '70%', y: '25%', label: 'Laptop' },
    { id: 'phone1', icon: Smartphone, x: '80%', y: '70%', label: 'Phone' },
    { id: 'phone2', icon: Smartphone, x: '30%', y: '80%', label: 'Phone' },
  ];

  // Mesh node positions
  const meshNodes = [
    { id: 'main', x: '50%', y: '50%', label: 'Main Router' },
    { id: 'node1', x: '25%', y: '55%', label: 'Mesh Node' },
    { id: 'node2', x: '75%', y: '45%', label: 'Mesh Node' },
  ];

  // Signal animation variants
  const signalVariants = {
    initial: { scale: 0, opacity: 0.8 },
    animate: { 
      scale: 3, 
      opacity: 0,
      transition: { 
        duration: 2,
        repeat: isAnimating ? Infinity : 0,
        repeatDelay: 0.5
      }
    }
  };

  // Data packet animation variants
  const packetVariants = {
    initial: (custom) => ({
      x: custom.startX,
      y: custom.startY,
      opacity: 0,
      scale: 0.5
    }),
    animate: (custom) => ({
      x: custom.endX,
      y: custom.endY,
      opacity: [0, 1, 1, 0],
      scale: [0.5, 1, 1, 0.5],
      transition: {
        duration: 1.5,
        times: [0, 0.2, 0.8, 1],
        delay: custom.delay,
        repeat: isAnimating ? Infinity : 0,
        repeatDelay: 3
      }
    })
  };

  // Calculate data packet paths
  const dataPackets = [
    // Internet to main router
    { 
      id: 'internet-main', 
      startX: '50%', startY: '0%', 
      endX: '50%', endY: '50%',
      delay: 0
    },
    // Main router to node 1
    { 
      id: 'main-node1', 
      startX: '50%', startY: '50%', 
      endX: '25%', endY: '55%',
      delay: 0.5
    },
    // Main router to node 2
    { 
      id: 'main-node2', 
      startX: '50%', startY: '50%', 
      endX: '75%', endY: '45%',
      delay: 0.5
    },
    // Node 1 to TV
    { 
      id: 'node1-tv', 
      startX: '25%', startY: '55%', 
      endX: '20%', endY: '30%',
      delay: 1.0
    },
    // Node 1 to Phone 2
    { 
      id: 'node1-phone2', 
      startX: '25%', startY: '55%', 
      endX: '30%', endY: '80%',
      delay: 1.2
    },
    // Node 2 to Laptop
    { 
      id: 'node2-laptop', 
      startX: '75%', startY: '45%', 
      endX: '70%', endY: '25%',
      delay: 1.0
    },
    // Node 2 to Phone 1
    { 
      id: 'node2-phone1', 
      startX: '75%', startY: '45%', 
      endX: '80%', endY: '70%',
      delay: 1.2
    },
  ];

  return (
    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors">
      <h3 className="text-purple-400 font-semibold text-xl mb-4">How Mesh Technology Works</h3>
      
      <div className="relative w-full h-[400px] bg-gray-900/50 rounded-lg mb-4 overflow-hidden">
        {/* Internet connection */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 flex flex-col items-center">
          <div className="bg-blue-500 p-2 rounded-full">
            <Wifi className="h-6 w-6 text-white" />
          </div>
          <span className="text-blue-400 text-sm mt-1">Internet</span>
        </div>
        
        {/* Mesh nodes */}
        {meshNodes.map((node) => (
          <div 
            key={node.id}
            className="absolute flex flex-col items-center"
            style={{ left: node.x, top: node.y, transform: 'translate(-50%, -50%)' }}
          >
            <div className="relative">
              <div className="bg-purple-600 p-3 rounded-full z-10 relative">
                <Router className="h-6 w-6 text-white" />
              </div>
              
              {/* Signal waves animation */}
              <motion.div
                className="absolute top-1/2 left-1/2 w-full h-full rounded-full border-2 border-purple-400/50 z-0"
                style={{ translateX: '-50%', translateY: '-50%' }}
                variants={signalVariants}
                initial="initial"
                animate={isAnimating ? "animate" : "initial"}
              />
            </div>
            <span className="text-purple-300 text-sm mt-2">{node.label}</span>
          </div>
        ))}
        
        {/* Devices */}
        {devices.map((device) => {
          const DeviceIcon = device.icon;
          return (
            <div 
              key={device.id}
              className="absolute flex flex-col items-center"
              style={{ left: device.x, top: device.y, transform: 'translate(-50%, -50%)' }}
            >
              <div className="bg-gray-700 p-2 rounded-full">
                <DeviceIcon className="h-5 w-5 text-gray-300" />
              </div>
              <span className="text-gray-400 text-xs mt-1">{device.label}</span>
            </div>
          );
        })}
        
        {/* Data packets */}
        {isAnimating && dataPackets.map((packet) => (
          <motion.div
            key={packet.id}
            className="absolute w-3 h-3 bg-blue-500 rounded-full shadow-lg shadow-blue-500/50 z-20"
            style={{ translateX: '-50%', translateY: '-50%' }}
            custom={{
              startX: packet.startX,
              startY: packet.startY,
              endX: packet.endX,
              endY: packet.endY,
              delay: packet.delay
            }}
            variants={packetVariants}
            initial="initial"
            animate="animate"
          />
        ))}
      </div>
      
      <div className="flex justify-center">
        <button
          className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-full transition-colors"
          onClick={startAnimation}
          disabled={isAnimating}
        >
          {isAnimating ? 'Animating...' : 'Start Animation'}
        </button>
      </div>
      
      <div className="mt-4 text-gray-300 text-sm">
        <p>
          Click the button above to see how data flows through a mesh network. The main router connects to the internet and communicates with mesh nodes, which extend coverage throughout your home. Devices automatically connect to the strongest signal.
        </p>
      </div>
    </div>
  );
};

export default MeshAnimation;